import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { API_ENDPOINTS, apiClient } from '../config/api';
import { auth } from '../firebase/config';
import { getActiveRoleFromStorage } from '../utils/getActiveRole';
import { SERVICE_TYPE_OPTIONS } from '../data/serviceTypes';
import { formatUSPhoneInput, getUSPhoneError, toE164US } from '../utils/phone';
import { getViewerTimezone } from '../utils/dateTime';
import './InviteSomeonePage.css';

// ── Custom transaction dropdown — shows 3 lines per row ──────────────────────
const TransactionDropdown = ({ transactions, value, onChange, placeholder, disabled, isLoading }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const selected = transactions.find(t => String(t.id) === String(value)) || null;

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = (id) => { onChange(id); setOpen(false); };

  return (
    <div className="isv-txn-dd" ref={ref}>
      <button
        type="button"
        className={`isv-txn-trigger${open ? ' isv-txn-trigger--open' : ''}`}
        onClick={() => !disabled && !isLoading && setOpen(o => !o)}
        disabled={disabled || isLoading}
      >
        <span className="isv-txn-trigger-content">
          {isLoading ? (
            <span className="isv-txn-placeholder">Loading transactions...</span>
          ) : selected ? (
            <>
              {selected.transactionNumber && (
                <span className="isv-txn-sel-num">{selected.transactionNumber}</span>
              )}
              <span className="isv-txn-sel-name">
                {selected.transactionName || selected.name || '—'}
              </span>
              {selected.propertyAddress && (
                <span className="isv-txn-sel-addr">{selected.propertyAddress}</span>
              )}
            </>
          ) : (
            <span className="isv-txn-placeholder">{placeholder}</span>
          )}
        </span>
        <svg
          className={`isv-txn-chevron${open ? ' isv-txn-chevron--up' : ''}`}
          width="14" height="14" viewBox="0 0 24 24"
          fill="none" stroke="currentColor" strokeWidth="2"
          strokeLinecap="round" strokeLinejoin="round"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <div className="isv-txn-menu">
          <div
            className={`isv-txn-option${!value ? ' isv-txn-option--active' : ''}`}
            onClick={() => handleSelect('')}
          >
            <span className="isv-txn-opt-none">{placeholder}</span>
          </div>
          {transactions.length === 0 ? (
            <div className="isv-txn-option isv-txn-opt-empty">No transactions found</div>
          ) : (
            transactions.map(txn => (
              <div
                key={txn.id}
                className={`isv-txn-option${String(txn.id) === String(value) ? ' isv-txn-option--active' : ''}`}
                onClick={() => handleSelect(String(txn.id))}
              >
                {txn.transactionNumber && (
                  <span className="isv-txn-opt-num">{txn.transactionNumber}</span>
                )}
                <span className="isv-txn-opt-name">
                  {txn.transactionName || txn.name || '—'}
                </span>
                {txn.propertyAddress && (
                  <span className="isv-txn-opt-addr">{txn.propertyAddress}</span>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

const InviteSomeonePage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Detect logged-in user role — always use the active/switched role
  const loggedInUserRole = getActiveRoleFromStorage();

  // ── Shared role definitions ──────────────────────────────────────────────────
  const allRoles = [
    { label: 'Agent', value: 'AGENT' },
    { label: 'Broker', value: 'BROKER' },
    { label: 'Broker Manager', value: 'BROKER_MANAGER' },
    { label: 'Broker Admin', value: 'BROKER_ADMIN' },
    { label: 'Brokerage', value: 'BROKERAGE' },
    { label: 'Buyer', value: 'BUYER' },
    { label: 'Seller', value: 'SELLER' },
    { label: 'Transaction Coordinator', value: 'TRANSACTION_COORDINATOR' },
    { label: 'Vendor', value: 'SERVICE_PROVIDER' },
  ];

  const INVITE_PERMISSIONS = {
    AGENT: ['AGENT', 'BROKER', 'BROKER_MANAGER', 'BROKER_ADMIN', 'BROKERAGE', 'BUYER', 'SELLER', 'TRANSACTION_COORDINATOR', 'SERVICE_PROVIDER'],
    BROKER: ['AGENT', 'BROKER', 'BROKER_MANAGER', 'BROKER_ADMIN', 'BROKERAGE', 'BUYER', 'SELLER', 'TRANSACTION_COORDINATOR', 'SERVICE_PROVIDER'],
    BROKERAGE: ['AGENT', 'BROKER', 'BROKER_MANAGER', 'BROKER_ADMIN', 'BROKERAGE', 'BUYER', 'SELLER', 'TRANSACTION_COORDINATOR', 'SERVICE_PROVIDER'],
    BROKER_MANAGER: ['AGENT', 'BROKER', 'BROKER_MANAGER', 'BROKER_ADMIN', 'BROKERAGE', 'BUYER', 'SELLER', 'TRANSACTION_COORDINATOR', 'SERVICE_PROVIDER'],
    BROKER_ADMIN: ['AGENT', 'BROKER', 'BROKER_MANAGER', 'BROKER_ADMIN', 'BROKERAGE', 'BUYER', 'SELLER', 'TRANSACTION_COORDINATOR', 'SERVICE_PROVIDER'],
    TRANSACTION_COORDINATOR: ['AGENT', 'BROKER', 'BROKER_MANAGER', 'BROKER_ADMIN', 'BROKERAGE', 'BUYER', 'SELLER', 'TRANSACTION_COORDINATOR', 'SERVICE_PROVIDER'],
    BUYER: ['AGENT', 'BUYER', 'SELLER', 'SERVICE_PROVIDER'],
    SELLER: ['AGENT', 'BUYER', 'SELLER', 'SERVICE_PROVIDER'],
  };

  const allowedValues = INVITE_PERMISSIONS[loggedInUserRole] || ['AGENT', 'BUYER', 'SELLER', 'SERVICE_PROVIDER'];
  const roles = allRoles.filter(role => allowedValues.includes(role.value));

  const canSelectTransaction = loggedInUserRole === 'AGENT' || loggedInUserRole === 'TRANSACTION_COORDINATOR';

  // ── Active tab ────────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState('direct'); // 'direct' | 'shareable'

  // ── Direct Invite state ───────────────────────────────────────────────────────
  // No default role — user must select one (mirrors mobile InviteSomeoneScreen).
  const [selectedRole, setSelectedRole] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  // Brokerage-specific fields (shown only when selectedRole === 'BROKERAGE')
  const [brokerageCompanyName, setBrokerageCompanyName] = useState('');
  const [brokerageLicenceId, setBrokerageLicenceId] = useState('');
  // Vendor-specific field (shown only when selectedRole === 'SERVICE_PROVIDER') — matches mobile InviteSomeoneScreen
  const [vendorServiceType, setVendorServiceType] = useState('');
  // Vendor Type — Individual (default, unchanged flow) or Company (business
  // name replaces first/last name). Only meaningful when selectedRole === 'SERVICE_PROVIDER'.
  const [vendorType, setVendorType] = useState('INDIVIDUAL');
  const [vendorBusinessName, setVendorBusinessName] = useState('');
  const isCompanyVendor = selectedRole === 'SERVICE_PROVIDER' && vendorType === 'COMPANY';
  // Service-type picker catalog — seeded with the offline fallback, overwritten
  // by the live GET /api/v1/service-types fetch below (mirrors mobile's
  // InviteSomeoneScreen).
  const [serviceTypeOptions, setServiceTypeOptions] = useState(SERVICE_TYPE_OPTIONS);

  // Pre-fill email when navigated here from the Add Contact modal's "Send invite" button
  // Pre-fill role when navigated from the Assign-SP modal's "Invite a Provider" CTA
  // (avoids sending Agents to the SP-only /invite-inspector page which bounces them
  // to /dashboard — see AssignServiceProviderModal wrong-route bug 2026-09-29).
  useEffect(() => {
    const prefill = location.state?.prefillEmail;
    if (prefill) setEmail(prefill);
    const prefillRole = location.state?.prefillRole;
    if (prefillRole && INVITE_PERMISSIONS[loggedInUserRole]?.includes(prefillRole)) {
      setSelectedRole(prefillRole);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [welcomeNote, setWelcomeNote] = useState('');
  const [selectedTransaction, setSelectedTransaction] = useState('');
  // Contact mode is forced to Email — Text and Both options were removed
  // from the Invite Someone flow (web + mobile parity).
  const [contactMode] = useState('Email');
  const [fieldErrors, setFieldErrors] = useState({});
  const [isSending, setIsSending] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [linkCopied, setLinkCopied] = useState(false);
  const [isCopyingLink, setIsCopyingLink] = useState(false);
  const [pendingResendEmail, setPendingResendEmail] = useState(null);

  // ── Shareable Link state ──────────────────────────────────────────────────────
  // No default role — matches Direct Invite / mobile (no role pre-selected).
  const [shareableRole, setShareableRole] = useState('');
  // Vendor Type — Individual (default) or Company. Only meaningful when
  // shareableRole === 'SERVICE_PROVIDER'. Mirrors Direct Invite's vendorType.
  const [shareableVendorType, setShareableVendorType] = useState('INDIVIDUAL');
  // Company name — optional when shareableVendorType === 'COMPANY' (unlike
  // Direct Invite's required vendorBusinessName); sent only when non-blank.
  const [shareableBusinessName, setShareableBusinessName] = useState('');
  const [shareableTransaction, setShareableTransaction] = useState('');
  const [usageLimit, setUsageLimit] = useState('single'); // 'single' | 'unlimited'
  const [isGeneratingLink, setIsGeneratingLink] = useState(false);
  const [generatedLink, setGeneratedLink] = useState('');
  const [linkExpiryDate, setLinkExpiryDate] = useState(null);
  const [shareableLinkCopied, setShareableLinkCopied] = useState(false);
  const [shareableError, setShareableError] = useState('');

  // ── Shared: transactions ──────────────────────────────────────────────────────
  const [transactions, setTransactions] = useState([]);
  const [isLoadingTransactions, setIsLoadingTransactions] = useState(false);

  // Auto-hide success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Firebase token helper
  const getFirebaseToken = () => new Promise((resolve, reject) => {
    if (auth.currentUser) {
      auth.currentUser.getIdToken(true).then(resolve).catch(reject);
      return;
    }
    const timeoutId = setTimeout(() => { unsubscribe(); reject(new Error('Firebase auth timeout')); }, 10000);
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      clearTimeout(timeoutId);
      unsubscribe();
      if (user) {
        try { resolve(await user.getIdToken(true)); } catch (e) { reject(e); }
      } else {
        reject(new Error('No authenticated user'));
      }
    });
  });

  // Fetch the canonical service-type catalog once on mount. The fallback
  // list already seeds the picker so it's usable offline; this overwrites it
  // as soon as the response lands (mirrors mobile's InviteSomeoneScreen).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getFirebaseToken();
        const resp = await apiClient.get(API_ENDPOINTS.serviceTypes, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const catalog = Array.isArray(resp.data) ? resp.data : (resp.data?.content || resp.data?.data || []);
        if (!cancelled && catalog.length > 0) {
          setServiceTypeOptions(catalog.map(t => ({ id: t.id, name: t.label })));
        }
      } catch (error) {
        console.warn('[InviteSomeone] Failed to fetch service types, using fallback:', error);
      }
    })();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch transactions for dropdowns (only for Agent/TC roles)
  useEffect(() => {
    const userData = localStorage.getItem('user');
    const isAuthenticated = localStorage.getItem('isAuthenticated');
    if (!userData || !isAuthenticated) { navigate('/'); return; }
    if (!canSelectTransaction) return;

    const fetchTransactions = async () => {
      setIsLoadingTransactions(true);
      try {
        const firebaseToken = await getFirebaseToken();
        const response = await apiClient.get(API_ENDPOINTS.transactions, {
          headers: { 'Authorization': `Bearer ${firebaseToken}`, 'Content-Type': 'application/json' },
          params: {
            status: ['PENDING', 'ACTIVE', 'UNDER_CONTRACT', 'CLOSING'],
            viewingRole: 'AGENT',
          },
          paramsSerializer: (params) => {
            const sp = new URLSearchParams();
            for (const [key, value] of Object.entries(params)) {
              if (Array.isArray(value)) {
                value.forEach(v => sp.append(key, v));
              } else if (value !== undefined && value !== null) {
                sp.append(key, String(value));
              }
            }
            return sp.toString();
          },
        });
        const data = response.data?.content || response.data?.transactions || response.data?.transactionCards || (Array.isArray(response.data) ? response.data : []);
        setTransactions(data);
      } catch (err) {
        console.error('Error fetching transactions:', err);
      } finally {
        setIsLoadingTransactions(false);
      }
    };
    fetchTransactions();
  }, [navigate, canSelectTransaction]); // eslint-disable-line react-hooks/exhaustive-deps

  const getLoggedInUser = () => {
    try { return JSON.parse(localStorage.getItem('user')); } catch { return null; }
  };

  const getSelectedTransactionObj = () =>
    transactions.find(txn => String(txn.id) === String(selectedTransaction)) || null;

  const getTransactionLabel = (txn) => {
    const address = txn.propertyAddress || txn.dealName || 'No address';
    const type = txn.type === 'LISTING' || txn.type === 'SALE' ? 'Listing' : 'Active Purchase';
    const status = txn.status ? ` - ${txn.status.charAt(0) + txn.status.slice(1).toLowerCase()}` : '';
    return `${address} - ${type}${status}`;
  };

  // ── Direct Invite helpers ────────────────────────────────────────────────────
  const validateFields = () => {
    const errors = {};
    if (selectedRole === 'BROKERAGE') {
      if (!brokerageCompanyName.trim()) errors.brokerageCompanyName = 'Company name is required';
      if (!email.trim()) {
        errors.email = 'Email address is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
        errors.email = 'Please enter a valid email address';
      }
    } else if (isCompanyVendor) {
      if (!vendorBusinessName.trim()) errors.vendorBusinessName = 'Business name is required';
      if (!email.trim()) {
        errors.email = 'Email address is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
        errors.email = 'Please enter a valid email address';
      }
      // Same phone rule as the standard vendor form below.
      const phoneErr = getUSPhoneError(phone, { required: contactMode !== 'Email' });
      if (phoneErr) errors.phone = phoneErr;
    } else {
      if (!firstName.trim()) errors.firstName = 'First name is required';
      if (!lastName.trim()) errors.lastName = 'Last name is required';
      if (!email.trim()) {
        errors.email = 'Email address is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
        errors.email = 'Please enter a valid email address';
      }
      // Phone is only mandatory when the invite will actually be texted — matches
      // mobile's isValidPhone() gating (required for Text/Both, optional for Email).
      const phoneErr = getUSPhoneError(phone, { required: contactMode !== 'Email' });
      if (phoneErr) errors.phone = phoneErr;
    }
    if (!selectedRole) errors.role = 'Please select a role';
    // Service type is mandatory once Vendor is selected — mirrors the request
    // payload requirement (backend resolves this id to a canonical label).
    if (selectedRole === 'SERVICE_PROVIDER' && !vendorServiceType) {
      errors.vendorServiceType = 'Please select a service type';
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFieldChange = (field, setter) => (e) => {
    setter(e.target.value);
    if (fieldErrors[field]) setFieldErrors(prev => { const n = { ...prev }; delete n[field]; return n; });
  };

  const handlePhoneFieldChange = (setter) => (e) => {
    setter(formatUSPhoneInput(e.target.value));
    if (fieldErrors.phone) setFieldErrors(prev => { const n = { ...prev }; delete n.phone; return n; });
  };

  const roleToClientType = {
    BUYER: 'buyer', SELLER: 'seller', AGENT: 'BUYERS_AGENT',
    BROKER: 'BROKER', BROKER_MANAGER: 'BROKER_MANAGER', BROKER_ADMIN: 'BROKER_ADMIN',
    BROKERAGE: 'BROKERAGE',
    TRANSACTION_COORDINATOR: 'TRANSACTION_COORDINATOR', SERVICE_PROVIDER: 'SERVICE_PROVIDER',
  };

  const sendInviteEmail = async (invitationKey, authHeaders, user, selectedTxnObj, inviterName) => {
    const inviteeName = selectedRole === 'BROKERAGE'
      ? brokerageCompanyName.trim()
      : isCompanyVendor
      ? vendorBusinessName.trim()
      : [firstName.trim(), lastName.trim()].filter(Boolean).join(' ');
    await apiClient.post(API_ENDPOINTS.sendInviteEmail, {
      invitationKey,
      email: email.trim(),
      inviteeName,
      role: selectedRole,
      inviterName,
      inviterEmail: user?.email,
      inviterPhone: user?.phone,
      welcomeNote: welcomeNote.trim() || undefined,
      transactionId: selectedRole !== 'BROKERAGE' ? (selectedTransaction || undefined) : undefined,
      propertyAddress: selectedRole !== 'BROKERAGE' ? (selectedTxnObj?.propertyAddress || undefined) : undefined,
    }, { headers: authHeaders });
  };

  const resetForm = () => {
    setFirstName(''); setLastName(''); setEmail(''); setPhone(''); setWelcomeNote('');
    setBrokerageCompanyName(''); setBrokerageLicenceId(''); setVendorServiceType('');
    setVendorType('INDIVIDUAL'); setVendorBusinessName('');
    setSelectedTransaction('');
    setFieldErrors({}); setPendingResendEmail(null);
  };

  const handleSendInvite = async () => {
    if (!validateFields()) return;
    setIsSending(true);
    setErrorMessage(''); setSuccessMessage(''); setPendingResendEmail(null);
    try {
      const firebaseToken = await getFirebaseToken();
      const user = getLoggedInUser();
      const selectedTxnObj = getSelectedTransactionObj();
      const inviterName = user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '';
      const authHeaders = { 'Authorization': `Bearer ${firebaseToken}`, 'Content-Type': 'application/json' };
      // Backend requires E.164 for phone when present (mirrors mobile's invite.service.ts
      // normalization) — validateFields() above guarantees phone, if provided, is a valid
      // 10-digit US number; it's optional when Contact Mode is Email-only.
      const normalizedPhone = phone.trim() ? toE164US(phone) : '';
      const clientType = roleToClientType[selectedRole] || selectedRole.toLowerCase();
      let invitationKey = null;

      if (selectedRole === 'BROKERAGE') {
        const invitationResponse = await apiClient.post(API_ENDPOINTS.createInvitation, {
          role: 'BROKERAGE', isShareable: false,
          email: email.trim(),
          companyName: brokerageCompanyName.trim(),
          brokerageLicenseId: brokerageLicenceId.trim() || undefined,
        }, { headers: authHeaders });
        invitationKey = invitationResponse.data?.invitationKey || invitationResponse.data?.key ||
          invitationResponse.data?.data?.invitationKey;
      } else if (selectedTransaction) {
        let existingUserId = null;
        try {
          const checkRes = await apiClient.post(API_ENDPOINTS.checkEmail, { email: email.trim() }, { headers: authHeaders });
          if (checkRes.data?.exists && checkRes.data?.userId) existingUserId = checkRes.data.userId;
        } catch (checkErr) {
          if (checkErr.response?.status !== 404) console.warn('Email check error:', checkErr);
        }

        if (existingUserId) {
          await apiClient.post(API_ENDPOINTS.transactionClients(selectedTransaction), { userId: existingUserId, role: clientType.toUpperCase() }, { headers: authHeaders });
          setSuccessMessage('Added to transaction successfully!');
          resetForm(); return;
        }

        const inviteRes = await apiClient.post(API_ENDPOINTS.inviteToTransaction(selectedTransaction), {
          transactionId: selectedTransaction, clientType,
          email: email.trim(), firstName: firstName.trim() || undefined,
          lastName: lastName.trim() || undefined, phone: normalizedPhone || undefined,
          serviceType: selectedRole === 'SERVICE_PROVIDER' ? (vendorServiceType || undefined) : undefined,
        }, { headers: authHeaders });
        invitationKey = inviteRes.data?.invitationKey || inviteRes.data?.key ||
          inviteRes.data?.data?.invitationKey || inviteRes.data?.invitation?.invitationKey;
      } else {
        const invitationResponse = await apiClient.post(API_ENDPOINTS.createInvitation, {
          role: selectedRole, isShareable: false,
          email: email.trim(), phone: normalizedPhone || undefined,
          firstName: isCompanyVendor ? undefined : firstName.trim(),
          lastName: isCompanyVendor ? undefined : lastName.trim(),
          serviceType: selectedRole === 'SERVICE_PROVIDER' ? (vendorServiceType || undefined) : undefined,
          vendorType: selectedRole === 'SERVICE_PROVIDER' ? vendorType : undefined,
          businessName: isCompanyVendor ? vendorBusinessName.trim() : undefined,
        }, { headers: authHeaders });
        invitationKey = invitationResponse.data?.invitationKey || invitationResponse.data?.key ||
          invitationResponse.data?.data?.invitationKey;
      }

      if (invitationKey) {
        try { await sendInviteEmail(invitationKey, authHeaders, user, selectedTxnObj, inviterName); }
        catch (emailErr) { console.warn('Email send failed but invitation was created:', emailErr); }
      }
      setSuccessMessage('Invitation sent successfully!');
      resetForm();
    } catch (error) {
      const rawMsg = error.response?.data?.message || error.response?.data?.error || error.message || '';
      if (rawMsg.toLowerCase().includes('pending invitation already exists')) {
        setErrorMessage(`A pending invitation already exists for ${email.trim()}. Click "Resend" below to resend the invitation email.`);
        setPendingResendEmail(email.trim()); return;
      }
      const isPermError = rawMsg.toLowerCase().includes('user_invite') ||
        (error.response?.status === 403 && rawMsg.toLowerCase().includes('permission'));
      if (isPermError) {
        setErrorMessage('To send an invite, please select a transaction from the dropdown and try again — or use "Copy Link" to share the invite URL directly.');
        return;
      }
      setErrorMessage(rawMsg || 'Failed to send invitation. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  const handleResendInvitation = async () => {
    setIsSending(true);
    setErrorMessage(''); setSuccessMessage('');
    try {
      const firebaseToken = await getFirebaseToken();
      const user = getLoggedInUser();
      const selectedTxnObj = getSelectedTransactionObj();
      const inviterName = user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '';
      const authHeaders = { 'Authorization': `Bearer ${firebaseToken}`, 'Content-Type': 'application/json' };
      const response = await apiClient.post(API_ENDPOINTS.resendInvitation, { email: email.trim(), role: selectedRole }, { headers: authHeaders });
      const invitationKey = response.data?.invitationKey || response.data?.key || response.data?.data?.invitationKey;
      if (invitationKey) {
        try { await sendInviteEmail(invitationKey, authHeaders, user, selectedTxnObj, inviterName); }
        catch (emailErr) { console.warn('Email send failed but invitation was resent:', emailErr); }
      }
      setSuccessMessage('Invitation resent successfully!');
      resetForm();
    } catch (error) {
      setErrorMessage(error.response?.data?.message || error.message || 'Failed to resend invitation. Please try again.');
      setPendingResendEmail(null);
    } finally {
      setIsSending(false);
    }
  };

  const handleCopyLink = async () => {
    if (!validateFields()) return;
    setLinkCopied(false); setErrorMessage(''); setIsCopyingLink(true);
    try {
      const firebaseToken = await getFirebaseToken();
      const authHeaders = { 'Authorization': `Bearer ${firebaseToken}`, 'Content-Type': 'application/json' };
      const payload = selectedRole === 'BROKERAGE'
        ? { role: 'BROKERAGE', isShareable: false, email: email.trim(), companyName: brokerageCompanyName.trim(), brokerageLicenseId: brokerageLicenceId.trim() || undefined }
        : isCompanyVendor
        ? { role: selectedRole, isShareable: false, email: email.trim(), phone: phone.trim() ? toE164US(phone) : '', businessName: vendorBusinessName.trim() }
        : { role: selectedRole, isShareable: false, email: email.trim(), phone: phone.trim() ? toE164US(phone) : '', firstName: firstName.trim(), lastName: lastName.trim() };
      if (selectedRole !== 'BROKERAGE' && !isCompanyVendor && selectedTransaction) payload.transactionId = selectedTransaction;
      if (selectedRole === 'SERVICE_PROVIDER' && vendorServiceType) payload.serviceType = vendorServiceType;
      if (selectedRole === 'SERVICE_PROVIDER') payload.vendorType = vendorType;
      const response = await apiClient.post(API_ENDPOINTS.createInvitation, payload, { headers: authHeaders });
      const data = response.data || {};
      const inviteLink = data.link || data.inviteLink || data.invitationLink || data.url ||
        (() => {
          const key = data.invitationKey || data.key || data.data?.invitationKey || data.data?.key;
          return key ? `${window.location.origin}/invite?key=${key}&type=user` : null;
        })();
      if (!inviteLink) { setErrorMessage('Invitation created but no link was returned. Please try again.'); return; }
      await navigator.clipboard.writeText(inviteLink);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 3000);
    } catch (err) {
      const rawMsg = err.response?.data?.message || err.response?.data?.error || '';
      if (rawMsg.toLowerCase().includes('pending invitation already exists')) {
        setErrorMessage(`A pending invitation already exists for ${email.trim()}. Use "Send Invite" → "Resend" instead.`);
        setPendingResendEmail(email.trim());
      } else {
        setErrorMessage(rawMsg || 'Failed to generate invite link. Please try again.');
      }
    } finally {
      setIsCopyingLink(false);
    }
  };

  // ── Shareable Link handlers ──────────────────────────────────────────────────
  const handleGenerateShareableLink = async () => {
    if (!shareableRole) { setShareableError('Please select a role.'); return; }
    // Company name is optional for shareable Company-vendor links — the
    // registrant enters it on the register form, where it is still required.
    setIsGeneratingLink(true);
    setShareableError('');
    try {
      const firebaseToken = await getFirebaseToken();
      const isShareableVendor = shareableRole === 'SERVICE_PROVIDER';
      const isShareableCompanyVendor = isShareableVendor && shareableVendorType === 'COMPANY';
      const payload = {
        role: shareableRole,
        isShareable: true,
        expiresInDays: 7,
        usageLimit,
      };
      // Company vendors are not tied to a specific transaction — same rule as direct invite.
      if (shareableTransaction && !isShareableCompanyVendor) payload.transactionId = shareableTransaction;
      if (isShareableVendor) payload.vendorType = shareableVendorType;
      if (isShareableCompanyVendor && shareableBusinessName.trim()) payload.businessName = shareableBusinessName.trim();
      const response = await apiClient.post(API_ENDPOINTS.createInvitation, payload, {
        headers: { 'Authorization': `Bearer ${firebaseToken}`, 'Content-Type': 'application/json' },
      });
      const data = response.data || {};
      const link = data.invitationLink || data.link || data.inviteLink || data.url ||
        (() => {
          const key = data.invitationKey || data.key || data.data?.invitationKey;
          return key ? `${window.location.origin}/invite?key=${key}&type=user` : null;
        })();
      if (!link) { setShareableError('Link generated but no URL was returned. Please try again.'); return; }
      setGeneratedLink(link);
      // Backend may return this as a bare LocalDateTime (UTC digits, no 'Z') —
      // append it if missing so this isn't misparsed as browser-local time.
      setLinkExpiryDate(data.expiresAt
        ? new Date(/[Zz]|[+-]\d{2}:\d{2}$/.test(data.expiresAt) ? data.expiresAt : `${data.expiresAt}Z`)
        : null);
    } catch (err) {
      setShareableError(err.response?.data?.message || err.message || 'Failed to generate link. Please try again.');
    } finally {
      setIsGeneratingLink(false);
    }
  };

  const handleCopyShareableLink = async () => {
    try {
      await navigator.clipboard.writeText(generatedLink);
      setShareableLinkCopied(true);
      setTimeout(() => setShareableLinkCopied(false), 3000);
    } catch {
      setShareableError('Failed to copy link. Please copy it manually.');
    }
  };

  const handleShareLink = async () => {
    const roleLabel = roles.find(r => r.value === shareableRole)?.label || shareableRole;
    const expiryStr = linkExpiryDate
      ? linkExpiryDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: getViewerTimezone() })
      : '';
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Rialty Invitation',
          text: `You're invited to join Rialty as a ${roleLabel}!\n\nClick the link below to register:\n${generatedLink}${expiryStr ? `\n\nThis link expires on ${expiryStr}.` : ''}`,
          url: generatedLink,
        });
      } catch (err) {
        if (err.name !== 'AbortError') handleCopyShareableLink();
      }
    } else {
      handleCopyShareableLink();
    }
  };

  const handleNewLink = () => {
    setGeneratedLink('');
    setLinkExpiryDate(null);
    setShareableLinkCopied(false);
  };

  const handleTabSwitch = (tab) => {
    setActiveTab(tab);
    setSuccessMessage('');
    setErrorMessage('');
    setShareableError('');
  };

  const formatExpiry = (date) => {
    if (!date) return '';
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: getViewerTimezone() });
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="dashboard-exact-container">
      <main className="isv-main">
        <div className="isv-card">

          {/* Header */}
          <div className="isv-header">
            <div>
              <h1 className="isv-title">Invite Someone</h1>
              <p className="isv-subtitle">Add a party to a transaction</p>
            </div>
          </div>

          <div className="isv-divider" />

          {/* Tab Selector */}
          <div className="isv-tabs">
            <button
              className={`isv-tab-btn ${activeTab === 'direct' ? 'isv-tab-btn-active' : ''}`}
              onClick={() => handleTabSwitch('direct')}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
              Direct Invite
            </button>
            <button
              className={`isv-tab-btn ${activeTab === 'shareable' ? 'isv-tab-btn-active' : ''}`}
              onClick={() => handleTabSwitch('shareable')}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
              </svg>
              Shareable Link
            </button>
          </div>

          {/* ── TAB 1: Direct Invite ─────────────────────────────────────────── */}
          {activeTab === 'direct' && (
            <>
              {/* Role Selection */}
              <div className="isv-section">
                <label className="isv-label">
                  INVITE AS (SELECT ONE)
                  <span className="isv-required-marker" aria-hidden="true"> *</span>
                </label>
                <div className="isv-select-group">
                  <select
                    className={`isv-select ${fieldErrors.role ? 'isv-input-error' : ''}`}
                    value={selectedRole}
                    onChange={(e) => {
                      setSelectedRole(e.target.value);
                      if (fieldErrors.role) {
                        setFieldErrors((prev) => {
                          const n = { ...prev };
                          delete n.role;
                          return n;
                        });
                      }
                    }}
                    aria-required="true"
                  >
                    <option value="" disabled>Select a role</option>
                    {roles.map((role) => (
                      <option key={role.value} value={role.value}>{role.label}</option>
                    ))}
                  </select>
                </div>
                {fieldErrors.role && <span className="isv-field-error">{fieldErrors.role}</span>}
              </div>

              {/* Vendor Type — Vendor only: Individual (unchanged flow) vs Company (business name) */}
              {selectedRole === 'SERVICE_PROVIDER' && (
                <div className="isv-section">
                  <label className="isv-label">VENDOR TYPE</label>
                  <div className="isv-contact-modes">
                    {[{ label: 'Individual', value: 'INDIVIDUAL' }, { label: 'Company', value: 'COMPANY' }].map((opt) => (
                      <button
                        key={opt.value}
                        className={`isv-mode-btn ${vendorType === opt.value ? 'isv-mode-btn-active' : ''}`}
                        onClick={() => setVendorType(opt.value)}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Service Type — Vendor only, matches mobile InviteSomeoneScreen */}
              {selectedRole === 'SERVICE_PROVIDER' && (
                <div className="isv-section">
                  <label className="isv-label">
                    SERVICE TYPE
                    <span className="isv-required-marker" aria-hidden="true"> *</span>
                  </label>
                  <div className="isv-select-group">
                    <select
                      className={`isv-select ${fieldErrors.vendorServiceType ? 'isv-input-error' : ''}`}
                      value={vendorServiceType}
                      onChange={handleFieldChange('vendorServiceType', setVendorServiceType)}
                      aria-required="true"
                    >
                      <option value="">Select Service Type</option>
                      {serviceTypeOptions.map((opt) => (
                        <option key={opt.id} value={opt.id}>{opt.name}</option>
                      ))}
                    </select>
                    <span className="isv-select-hint">Select the vendor's service type</span>
                    {fieldErrors.vendorServiceType && <span className="isv-field-error">{fieldErrors.vendorServiceType}</span>}
                  </div>
                </div>
              )}

              {/* Invitation Details */}
              <div className="isv-section">
                <label className="isv-label">INVITATION DETAILS</label>

                {selectedRole === 'BROKERAGE' ? (
                  /* ── Brokerage-specific field design ──────────────────── */
                  <div className="isv-brk-form">
                    <div className="isv-brk-field">
                      <label className="isv-brk-label">
                        Company name <span className="isv-brk-required">*</span>
                      </label>
                      <input
                        type="text"
                        className={`isv-brk-input${fieldErrors.brokerageCompanyName ? ' isv-brk-input-error' : ''}`}
                        placeholder="e.g. Gold Coast Realty Group LLC"
                        value={brokerageCompanyName}
                        onChange={handleFieldChange('brokerageCompanyName', setBrokerageCompanyName)}
                      />
                      {fieldErrors.brokerageCompanyName && <span className="isv-field-error">{fieldErrors.brokerageCompanyName}</span>}
                    </div>

                    <div className="isv-brk-field">
                      <label className="isv-brk-label">
                        Brokerage licence ID <span className="isv-brk-optional">(optional)</span>
                      </label>
                      <input
                        type="text"
                        className="isv-brk-input"
                        placeholder="Enter brokerage licence ID if known"
                        value={brokerageLicenceId}
                        onChange={(e) => setBrokerageLicenceId(e.target.value)}
                      />
                    </div>

                    <div className="isv-brk-field">
                      <label className="isv-brk-label">
                        Email address <span className="isv-brk-required">*</span>
                      </label>
                      <input
                        type="email"
                        className={`isv-brk-input${fieldErrors.email ? ' isv-brk-input-error' : ''}`}
                        placeholder="Contact email address"
                        value={email}
                        onChange={handleFieldChange('email', setEmail)}
                      />
                      {fieldErrors.email && <span className="isv-field-error">{fieldErrors.email}</span>}
                      <span className="isv-email-hint">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" /></svg>
                        Invite will be sent to this email address.
                      </span>
                    </div>

                    <div className="isv-brk-field">
                      <label className="isv-brk-label">
                        Welcome note <span className="isv-brk-optional">(optional)</span>
                      </label>
                      <textarea
                        className="isv-brk-textarea"
                        placeholder="Add a personal note to the invite email"
                        value={welcomeNote}
                        onChange={(e) => setWelcomeNote(e.target.value)}
                        rows={3}
                      />
                    </div>
                  </div>
                ) : isCompanyVendor ? (
                  /* ── Company vendor field design: Business Name replaces first/last name ── */
                  <>
                    <div className="isv-input-group">
                      <input
                        type="text"
                        className={`isv-input ${fieldErrors.vendorBusinessName ? 'isv-input-error' : ''}`}
                        placeholder="Business name *"
                        value={vendorBusinessName}
                        onChange={handleFieldChange('vendorBusinessName', setVendorBusinessName)}
                      />
                      <span className="isv-input-icon">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="2">
                          <path d="M3 21h18" /><path d="M5 21V7l8-4v18" /><path d="M19 21V11l-6-4" />
                        </svg>
                      </span>
                      {fieldErrors.vendorBusinessName && <span className="isv-field-error">{fieldErrors.vendorBusinessName}</span>}
                    </div>

                    <div className="isv-input-group">
                      <input
                        type="email"
                        className={`isv-input ${fieldErrors.email ? 'isv-input-error' : ''}`}
                        placeholder="Email address *"
                        value={email}
                        onChange={handleFieldChange('email', setEmail)}
                      />
                      <span className="isv-input-icon">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="2">
                          <rect x="2" y="4" width="20" height="16" rx="2" /><polyline points="22,7 12,13 2,7" />
                        </svg>
                      </span>
                      {fieldErrors.email && <span className="isv-field-error">{fieldErrors.email}</span>}
                      <span className="isv-email-hint">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" /></svg>
                        Invite will be sent to this email address.
                      </span>
                    </div>

                    <div className="isv-input-group">
                      <input
                        type="tel"
                        className={`isv-input ${fieldErrors.phone ? 'isv-input-error' : ''}`}
                        placeholder={contactMode === 'Email' ? 'Phone number (optional)' : 'Phone number *'}
                        value={phone}
                        onChange={handlePhoneFieldChange(setPhone)}
                      />
                      <span className="isv-input-icon">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#E11D48" strokeWidth="2">
                          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
                        </svg>
                      </span>
                      {fieldErrors.phone && <span className="isv-field-error">{fieldErrors.phone}</span>}
                    </div>

                    <div className="isv-textarea-group">
                      <textarea
                        className="isv-textarea"
                        placeholder="Welcome note (optional)"
                        value={welcomeNote}
                        onChange={(e) => setWelcomeNote(e.target.value)}
                        rows={3}
                      />
                      <span className="isv-textarea-hint">Add a personal message to the invitation</span>
                    </div>
                  </>
                ) : (
                  /* ── Standard field design (all other roles) ───────────── */
                  <>
                    <div className="isv-name-row">
                      <div className="isv-input-group">
                        <input
                          type="text"
                          className={`isv-input ${fieldErrors.firstName ? 'isv-input-error' : ''}`}
                          placeholder="First name *"
                          value={firstName}
                          onChange={handleFieldChange('firstName', setFirstName)}
                        />
                        <span className="isv-input-icon">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="2">
                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
                          </svg>
                        </span>
                        {fieldErrors.firstName && <span className="isv-field-error">{fieldErrors.firstName}</span>}
                      </div>
                      <div className="isv-input-group">
                        <input
                          type="text"
                          className={`isv-input ${fieldErrors.lastName ? 'isv-input-error' : ''}`}
                          placeholder="Last name *"
                          value={lastName}
                          onChange={handleFieldChange('lastName', setLastName)}
                        />
                        <span className="isv-input-icon">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="2">
                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
                          </svg>
                        </span>
                        {fieldErrors.lastName && <span className="isv-field-error">{fieldErrors.lastName}</span>}
                      </div>
                    </div>

                    <div className="isv-input-group">
                      <input
                        type="email"
                        className={`isv-input ${fieldErrors.email ? 'isv-input-error' : ''}`}
                        placeholder="Email address *"
                        value={email}
                        onChange={handleFieldChange('email', setEmail)}
                      />
                      <span className="isv-input-icon">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="2">
                          <rect x="2" y="4" width="20" height="16" rx="2" /><polyline points="22,7 12,13 2,7" />
                        </svg>
                      </span>
                      {fieldErrors.email && <span className="isv-field-error">{fieldErrors.email}</span>}
                      <span className="isv-email-hint">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" /></svg>
                        Invite will be sent to this email address.
                      </span>
                    </div>

                    <div className="isv-input-group">
                      <input
                        type="tel"
                        className={`isv-input ${fieldErrors.phone ? 'isv-input-error' : ''}`}
                        placeholder={contactMode === 'Email' ? 'Phone number (optional)' : 'Phone number *'}
                        value={phone}
                        onChange={handlePhoneFieldChange(setPhone)}
                      />
                      <span className="isv-input-icon">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#E11D48" strokeWidth="2">
                          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
                        </svg>
                      </span>
                      {fieldErrors.phone && <span className="isv-field-error">{fieldErrors.phone}</span>}
                    </div>

                    <div className="isv-textarea-group">
                      <textarea
                        className="isv-textarea"
                        placeholder="Welcome note (optional)"
                        value={welcomeNote}
                        onChange={(e) => setWelcomeNote(e.target.value)}
                        rows={3}
                      />
                      <span className="isv-textarea-hint">Add a personal message to the invitation</span>
                    </div>
                  </>
                )}
              </div>

              {/* Transaction Selection — Agent and TC only, hidden for Brokerage and Company vendor (no transaction access) */}
              {canSelectTransaction && selectedRole !== 'BROKERAGE' && !isCompanyVendor && (
                <div className="isv-section">
                  <div className="isv-select-group">
                    <TransactionDropdown
                      transactions={transactions}
                      value={selectedTransaction}
                      onChange={setSelectedTransaction}
                      placeholder="Select Transaction (optional)"
                      disabled={false}
                      isLoading={isLoadingTransactions}
                    />
                    <span className="isv-select-hint">Select which transaction to link this party to</span>
                  </div>
                </div>
              )}

              <div className="isv-divider" />

              {/* Actions */}
              <div className="isv-actions">
                <button
                  className={`isv-copy-btn ${linkCopied ? 'isv-copy-btn-success' : ''}`}
                  onClick={handleCopyLink}
                  disabled={isCopyingLink || isSending}
                >
                  {isCopyingLink ? (
                    <><span className="isv-spinner isv-spinner-dark" />Generating Link...</>
                  ) : linkCopied ? (
                    <><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>Link Copied!</>
                  ) : (
                    <><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></svg>Copy Link</>
                  )}
                </button>
                <button className="isv-send-btn" onClick={handleSendInvite} disabled={isSending}>
                  {isSending ? <span className="isv-spinner" /> : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></svg>}
                  {isSending ? 'Sending...' : 'Send Invite'}
                </button>
              </div>

              {/* Messages */}
              {successMessage && (
                <div className="isv-alert isv-alert-success" style={{ marginTop: 16 }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
                  <span className="isv-alert-text">{successMessage}</span>
                  <button className="isv-alert-dismiss" onClick={() => setSuccessMessage('')}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg></button>
                </div>
              )}
              {errorMessage && (
                <div className="isv-alert isv-alert-error" style={{ marginTop: 16 }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></svg>
                  <span className="isv-alert-text">{errorMessage}</span>
                  {pendingResendEmail && (
                    <button className="isv-send-btn" style={{ marginLeft: 8, padding: '6px 14px', fontSize: 13, minWidth: 'unset' }} onClick={handleResendInvitation} disabled={isSending}>
                      {isSending ? <span className="isv-spinner" /> : 'Resend'}
                    </button>
                  )}
                  <button className="isv-alert-dismiss" onClick={() => { setErrorMessage(''); setPendingResendEmail(null); }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg></button>
                </div>
              )}
            </>
          )}

          {/* ── TAB 2: Shareable Link ─────────────────────────────────────────── */}
          {activeTab === 'shareable' && (
            <>
              {/* Role Selection */}
              <div className="isv-section">
                <label className="isv-label">
                  INVITE AS (SELECT ONE)
                  <span className="isv-required-marker" aria-hidden="true"> *</span>
                </label>
                <div className="isv-select-group">
                  <select
                    className="isv-select"
                    value={shareableRole}
                    onChange={(e) => {
                      setShareableRole(e.target.value);
                      handleNewLink();
                    }}
                    aria-required="true"
                  >
                    <option value="" disabled>Select a role</option>
                    {roles.map((role) => (
                      <option key={role.value} value={role.value}>{role.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Vendor Type — Vendor only: Individual (unchanged flow) vs Company */}
              {shareableRole === 'SERVICE_PROVIDER' && (
                <div className="isv-section">
                  <label className="isv-label">VENDOR TYPE</label>
                  <div className="isv-contact-modes">
                    {[{ label: 'Individual', value: 'INDIVIDUAL' }, { label: 'Company', value: 'COMPANY' }].map((opt) => (
                      <button
                        key={opt.value}
                        className={`isv-mode-btn ${shareableVendorType === opt.value ? 'isv-mode-btn-active' : ''}`}
                        onClick={() => { setShareableVendorType(opt.value); handleNewLink(); }}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  {shareableVendorType === 'COMPANY' && (
                    <div className="isv-input-group" style={{ marginTop: 12 }}>
                      <input
                        type="text"
                        className="isv-input"
                        placeholder="Company name (optional)"
                        value={shareableBusinessName}
                        onChange={(e) => { setShareableBusinessName(e.target.value); if (shareableError) setShareableError(''); }}
                      />
                      <span className="isv-input-icon">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="2">
                          <path d="M3 21h18" /><path d="M5 21V7l8-4v18" /><path d="M19 21V11l-6-4" />
                        </svg>
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Link Settings */}
              <div className="isv-section">
                <label className="isv-label">LINK SETTINGS</label>

                {/* Link to Transaction — Agent and TC only. Company vendors are not tied to a transaction. */}
                {canSelectTransaction && !(shareableRole === 'SERVICE_PROVIDER' && shareableVendorType === 'COMPANY') && (
                  <div className="isv-select-group" style={{ marginBottom: 16 }}>
                    <TransactionDropdown
                      transactions={transactions}
                      value={shareableTransaction}
                      onChange={(id) => { setShareableTransaction(id); handleNewLink(); }}
                      placeholder="Link to Transaction (optional)"
                      disabled={false}
                      isLoading={isLoadingTransactions}
                    />
                    <span className="isv-select-hint">Optionally associate this link with a transaction</span>
                  </div>
                )}

                {/* Maximum Uses */}
                <label className="isv-label" style={{ marginBottom: 10 }}>MAXIMUM USES</label>
                <div className="isv-contact-modes">
                  {[
                    { label: 'Single-use', value: 'single' },
                    { label: 'Unlimited', value: 'unlimited' },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      className={`isv-mode-btn ${usageLimit === opt.value ? 'isv-mode-btn-active' : ''}`}
                      onClick={() => { setUsageLimit(opt.value); handleNewLink(); }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                <span className="isv-contact-hint">
                  {usageLimit === 'single' ? 'Link can only be used once' : 'Link can be used unlimited times'}
                </span>
              </div>

              <div className="isv-divider" />

              {/* Generated Link Box */}
              {generatedLink ? (
                <div className="isv-link-box">
                  <div className="isv-link-box-header">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#5B47D6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                    </svg>
                    <span className="isv-link-box-title">Shareable Invite Link</span>
                  </div>
                  <div className="isv-link-preview">{generatedLink}</div>
                  {linkExpiryDate && (
                    <div className="isv-link-expiry">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
                      Expires {formatExpiry(linkExpiryDate)}
                    </div>
                  )}
                  <div className="isv-link-actions">
                    <button
                      className={`isv-link-action-btn ${shareableLinkCopied ? 'isv-link-action-copied' : ''}`}
                      onClick={handleCopyShareableLink}
                    >
                      {shareableLinkCopied ? (
                        <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>Copied!</>
                      ) : (
                        <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>Copy</>
                      )}
                    </button>
                    <button className="isv-link-action-btn" onClick={handleShareLink}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
                        <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                      </svg>
                      Share
                    </button>
                    <button className="isv-link-action-btn isv-link-action-new" onClick={handleNewLink}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 1 0 .49-3.48" />
                      </svg>
                      New Link
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  className="isv-generate-btn"
                  onClick={handleGenerateShareableLink}
                  disabled={isGeneratingLink}
                >
                  {isGeneratingLink ? (
                    <><span className="isv-spinner" />Generating Link...</>
                  ) : (
                    <><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></svg>Generate Shareable Link</>
                  )}
                </button>
              )}

              {shareableError && (
                <div className="isv-alert isv-alert-error" style={{ marginTop: 16 }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></svg>
                  <span className="isv-alert-text">{shareableError}</span>
                  <button className="isv-alert-dismiss" onClick={() => setShareableError('')}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg></button>
                </div>
              )}
            </>
          )}

        </div>
      </main>
    </div>
  );
};

export default InviteSomeonePage;
